import { Provider } from 'near-api-js/lib/providers';
import { REF_FINANCE_ID } from '../../constants';
import {
  Amm,
  CreateSwapRouteInstructionsParams,
  Quote,
  QuoteParams,
  SwapMode,
  SwapParams,
} from '../common/types';
import { loadAllPools, loadPool } from './fetchers';
import { createRefTransactions } from './instructions';
import { getInputAmount, getOutputAmount } from './math';
import { getStableOutputAmount, getStableInputAmount } from './stable-math';
import { RefFinancePool } from './types';
import { filterMostLiquidUniqPools } from './utils';

class RefFinance implements Amm {
  id: string;
  contractId: string;
  instanceId: number;

  pool: RefFinancePool;
  isSimplePool: boolean;
  label: 'Ref.Finance';

  constructor(pool: RefFinancePool) {
    this.label = 'Ref.Finance';
    this.id = `${pool.id}`;
    this.contractId = REF_FINANCE_ID;
    this.instanceId = pool.id;

    this.pool = pool;
    this.isSimplePool = pool.pool_kind === 'SIMPLE_POOL';
  }

  static async loadPools({
    provider,
  }: {
    provider: Provider;
  }): Promise<RefFinance[]> {
    try {
      const { simplePools, stablePools } = await loadAllPools({
        provider,
        ammId: REF_FINANCE_ID,
      });

      const mostLiquidSimplePools = filterMostLiquidUniqPools(simplePools);

      return [...mostLiquidSimplePools, ...stablePools].map(
        (pool) => new RefFinance(pool)
      );
    } catch (e) {
      console.log('Error loading pools for Ref Finance', e);
    }

    return [];
  }

  getQuote(quoteParams: QuoteParams): Quote {
    const { inputMint, outputMint, amount, slippage, swapMode } = quoteParams;

    const feePct = this.pool.total_fee / 10000;

    if (swapMode === SwapMode.ExactIn) {
      if (this.isSimplePool) {
        const {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feeAmount,
          priceImpact,
        } = getOutputAmount({
          reserves: this.pool.reserves,
          poolFee: this.pool.total_fee,
          inputMint,
          outputMint,
          inputAmount: amount,
          slippage,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      } else {
        const {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feeAmount,
          priceImpact,
        } = getStableOutputAmount({
          tokenInId: inputMint,
          tokenOutId: outputMint,
          slippage,
          // @ts-ignore
          stablePool: this.pool,
          amountIn: amount,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      }
    } else if (swapMode === SwapMode.ExactOut) {
      if (this.isSimplePool) {
        const {
          amountIn,
          amountOut,
          minAmountOut,
          feeAmount,
          priceImpact,
          notEnoughLiquidity,
        } = getInputAmount({
          reserves: this.pool.reserves,
          poolFee: this.pool.total_fee,
          inputMint,
          outputMint,
          outputAmount: amount,
          slippage,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      } else {
        const {
          notEnoughLiquidity,
          feeAmount,
          amountIn,
          amountOut,
          minAmountOut,
          priceImpact,
        } = getStableInputAmount({
          tokenInId: inputMint,
          tokenOutId: outputMint,
          slippage,
          // @ts-ignore
          stablePool: this.pool,
          amountOut: amount,
        });

        return {
          notEnoughLiquidity,
          amountIn,
          amountOut,
          minAmountOut,
          feePct,
          feeMint: inputMint,
          feeAmount,
          priceImpact,
        };
      }
    }

    throw new Error('Unknown swap mode type');
  }

  async getPromiseForUpdate({
    provider,
  }: {
    provider: Provider;
  }): Promise<void> {
    return loadPool({
      provider,
      ammId: this.contractId,
      poolId: this.instanceId,
      poolKind: this.pool.pool_kind,
    }).then((pool) => {
      this.pool = pool;
    });
  }

  async createSwapInstructions(params: SwapParams) {
    const { user, swapStep } = params;

    const swapTransactions = await createRefTransactions({
      user,
      swapSteps: [swapStep],
    });

    console.log('swapTransactions', swapTransactions);

    return swapTransactions;
  }

  async createSwapRouteInstructions(params: CreateSwapRouteInstructionsParams) {
    const { user, swapRoute } = params;

    const swapTransactions = await createRefTransactions({
      user,
      swapSteps: swapRoute.steps,
    });

    console.log('swapTransactions', swapTransactions);

    return swapTransactions;
  }

  get reserveTokenMints() {
    return this.pool.token_account_ids;
  }
}

export { RefFinance };
